
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from "@/hooks/use-toast";
import { API_ENDPOINTS, getAuthHeaders, getApiUrl, getRoleEndpoint } from '@/utils/api-config';
import { ArrowLeft, Loader2 } from 'lucide-react';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayout';
import { PermissionProvider, usePermission } from '@/context/PermissionContext';
import type { Permission } from '@/context/PermissionContext';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import ModernButton from '@/components/dashboard/ModernButton';
import { ModernDropdown } from '@/components/ui/modern-dropdown';

interface RoleFeature {
  id: number;
  name: string;
  description: string;
  permissions: Permission[];
}

interface Role {
  id: number;
  name: string;
  description: string;
  feature_ids?: number[];
  features?: RoleFeature[];
  level?: number;
  is_active: boolean;
  created_at: string;
}

const resolveFeatureIds = (roleData: Role): number[] => {
  const idsFromPayload = Array.isArray(roleData.feature_ids) ? roleData.feature_ids : [];
  if (idsFromPayload.length) {
    return Array.from(new Set(idsFromPayload));
  }

  if (Array.isArray(roleData.features)) {
    return Array.from(
      new Set(
        roleData.features
          .map((feature) => feature?.id)
          .filter((id): id is number => typeof id === 'number')
      )
    );
  }

  return [];
};

const ROLE_LEVEL_OPTIONS = [1, 2, 3, 4, 5].map((levelNumber) => ({
  value: levelNumber.toString(),
  label: `Level ${levelNumber}`,
}));

const getDefaultRoleLevel = (incomingLevel?: number): string => {
  if (typeof incomingLevel === 'number') {
    const levelValue = incomingLevel.toString();
    const levelExists = ROLE_LEVEL_OPTIONS.some((option) => option.value === levelValue);
    if (levelExists) {
      return levelValue;
    }
  }

  return ROLE_LEVEL_OPTIONS[0].value;
};

const RoleEditorContent = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getToken } = useAuth();
  const { availablePermissionGroups, isLoading: isLoadingPermissions } = usePermission();
  const isEditMode = !!roleId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState<string>(getDefaultRoleLevel());
  const [isActive, setIsActive] = useState(true);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<number[]>([]);
  const [activeFeatureId, setActiveFeatureId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode && roleId) {
      fetchRoleDetails();
    }
  }, [roleId]);

  const fetchRoleDetails = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(getApiUrl(getRoleEndpoint(Number(roleId))), {
        method: 'GET',
        headers: getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error('Failed to fetch role details');
      }

      const result = await response.json();
      const roleData: Role = result.data;

      setName(roleData.name);
      setDescription(roleData.description);
      setLevel(getDefaultRoleLevel(roleData.level));
      setIsActive(roleData.is_active);
      const normalizedFeatureIds = resolveFeatureIds(roleData);

      setSelectedFeatureIds(normalizedFeatureIds);
      setActiveFeatureId(normalizedFeatureIds[0] ?? null);
    } catch (error) {
      console.error('Error fetching role details:', error);
      toast({
        title: "Error",
        description: "Failed to load role details.",
        variant: "destructive"
      });
      navigate('/settings/platform/security');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeatureToggle = (featureId: number, checked: boolean) => {
    setSelectedFeatureIds((current) => {
      const next = checked
        ? Array.from(new Set([...current, featureId]))
        : current.filter((id) => id !== featureId);
      return next;
    });

    setActiveFeatureId(featureId);
  };

  const isFeatureSelected = (featureId: number) => selectedFeatureIds.includes(featureId);

  useEffect(() => {
    if (!availablePermissionGroups.length || !selectedFeatureIds.length) {
      return;
    }

    const normalizedSelection = selectedFeatureIds.filter((featureId) =>
      availablePermissionGroups.some((group) => group.id === featureId)
    );

    const selectionChanged =
      normalizedSelection.length !== selectedFeatureIds.length ||
      normalizedSelection.some((id, index) => id !== selectedFeatureIds[index]);

    if (selectionChanged) {
      setSelectedFeatureIds(normalizedSelection);
    }
  }, [availablePermissionGroups, selectedFeatureIds]);

  useEffect(() => {
    if (!availablePermissionGroups.length) {
      if (activeFeatureId !== null) {
        setActiveFeatureId(null);
      }
      return;
    }

    const activeFeatureExists =
      activeFeatureId !== null && availablePermissionGroups.some((group) => group.id === activeFeatureId);

    if (activeFeatureExists) {
      return;
    }

    const availableIds = new Set(availablePermissionGroups.map((group) => group.id));
    const selectedFallback = selectedFeatureIds.find((featureId) => availableIds.has(featureId)) ?? null;
    const nextActiveId = selectedFallback ?? availablePermissionGroups[0]?.id ?? null;

    if (nextActiveId !== activeFeatureId) {
      setActiveFeatureId(nextActiveId);
    }
  }, [availablePermissionGroups, selectedFeatureIds, activeFeatureId]);

  const activeFeature = availablePermissionGroups.find((group) => group.id === activeFeatureId) || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      const token = getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const payload = {
        name,
        description,
        level: Number(level),
        feature_ids: selectedFeatureIds,
        is_active: isActive
      };

      let response;
      if (isEditMode) {
        response = await fetch(getApiUrl(getRoleEndpoint(Number(roleId))), {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(getApiUrl(API_ENDPOINTS.USER_ROLE), {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} role`);
      }

      toast({
        title: "Success",
        description: `Role ${isEditMode ? 'updated' : 'created'} successfully`,
      });

      navigate('/settings/platform/security');
    } catch (error) {
      console.error('Error saving role:', error);
      toast({
        title: "Error",
        description: `Failed to ${isEditMode ? 'update' : 'create'} role`,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PlatformSettingsLayout
        title={isEditMode ? "Edit Role" : "Create Role"}
        description={isEditMode ? "Update role details and permissions" : "Define a new role with specific permissions"}
      >
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" text="Loading role details..." />
        </div>
      </PlatformSettingsLayout>
    );
  }

  return (
    <PlatformSettingsLayout
      title={isEditMode ? "Edit Role" : "Create Role"}
      description={isEditMode ? "Update role details and permissions" : "Define a new role with specific permissions"}
    >
      <section className="p-8 bg-white dark:bg-neutral-800/50 rounded-2xl">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <ModernButton
              variant="outline"
              onClick={() => navigate('/settings/platform/security')}
              size='sm'
              iconOnly
              className="h-10 w-10 flex"
            >
              <ArrowLeft className="h-5 w-5" />
            </ModernButton>
            
          </div>
        </div>
        <div className="dark:text-gray-200 bg-neutral-50/80 dark:bg-neutral-800/70 rounded-xl border border-neutral-200/50 dark:border-none p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Role Name</Label>
                <Input 
                  id="name" 
                  value={name} 
                  variant="modern"
                  onChange={(e) => setName(e.target.value)} 
                  placeholder="e.g., Support Agent"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Role Level</Label>
                <ModernDropdown
                  value={level}
                  onValueChange={setLevel}
                  options={ROLE_LEVEL_OPTIONS}
                  placeholder="Select level"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <div className="flex items-center space-x-3 h-10">
                  <Switch 
                    id="active-status" 
                    checked={isActive}
                    onCheckedChange={setIsActive}
                  />
                  <Label htmlFor="active-status" className="!mt-0">
                    {isActive ? 'Active' : 'Inactive'}
                  </Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description" 
                value={description} 
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the role's responsibilities"
                rows={3}
              />
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="mb-1 block">Permission Sets</Label>
                <p className="text-sm text-muted-foreground">
                  Choose the access areas this role should include. Click a set to preview its permissions.
                </p>
              </div>
              {isLoadingPermissions ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" text="Loading permissions..." />
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    {availablePermissionGroups.map((feature) => (
                      <div
                        key={feature.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveFeatureId(feature.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setActiveFeatureId(feature.id);
                          }
                        }}
                        className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition focus:outline-none focus:ring-2 focus:ring-primary/60 ${
                          activeFeatureId === feature.id
                            ? 'border-primary/70 bg-primary/5'
                            : 'border-border bg-white/70 dark:bg-neutral-900/40'
                        }`}
                      >
                        <Checkbox
                          id={`feature-${feature.id}`}
                          checked={isFeatureSelected(feature.id)}
                          onCheckedChange={(checked) =>
                            handleFeatureToggle(feature.id, checked === true)
                          }
                          className='mt-1 rounded-[4px]'
                        />
                        <div>
                          <p className="text-sm font-semibold text-foreground">{feature.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border bg-white/70 dark:bg-neutral-900/40 p-4 space-y-4 min-h-[240px]">
                    {activeFeature ? (
                      <div className="h-full flex flex-col">
                        <div>
                          <p className="font-semibold text-sm text-foreground">{activeFeature.name}</p>
                          <p className="text-xs text-muted-foreground">{activeFeature.description}</p>
                        </div>
                        <ul className="mt-4 space-y-2 overflow-auto">
                          {activeFeature.permissions.map((permission) => (
                            <li key={permission.id} className="text-sm text-muted-foreground">
                              <span className="font-medium text-foreground">
                                {permission.name.replace(/_/g, ' ')}
                              </span>
                              <span className="block text-xs">{permission.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-muted-foreground text-center">
                        Select a permission set to preview its access.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <ModernButton 
                onClick={() => navigate('/settings/platform/security')} 
                type="button" 
                variant="outline"
                disabled={isSubmitting}
              >
                Cancel
              </ModernButton>
              <ModernButton 
                type="submit" 
                variant="primary"
                disabled={isSubmitting || !name || !level}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditMode ? 'Saving...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Save Changes' : 'Create Role'
                )}
              </ModernButton>
            </div>
          </form>
        </div>
      </section>
    </PlatformSettingsLayout>
  );
};

const RoleEditor = () => {
  return (
    <PermissionProvider>
      <RoleEditorContent />
    </PermissionProvider>
  );
};

export default RoleEditor;
